import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";

// Create a sound player with fallback to Web Audio API
const createSoundPlayer = () => {
  // Create audio context
  let audioContext: AudioContext | null = null;

  try {
    const AudioContextClass =
      window.AudioContext || (window as any).webkitAudioContext;
    if (AudioContextClass) {
      audioContext = new AudioContextClass();
    }
  } catch (e) {
    console.error("Failed to create AudioContext:", e);
  }

  // Define sound file paths with fallbacks
  const soundPaths = {
    timerComplete: "/sounds/timer-complete.mp3",
    restComplete: "/sounds/rest-complete.mp3",
    countdownBeep: "/sounds/countdown-beep.mp3",
    workoutComplete: "/sounds/workout-complete.mp3",
  };

  // Create audio elements
  const audioElements = {
    timerComplete: new Audio(soundPaths.timerComplete),
    restComplete: new Audio(soundPaths.restComplete),
    countdownBeep: new Audio(soundPaths.countdownBeep),
    workoutComplete: new Audio(soundPaths.workoutComplete),
  };

  // Track if sounds are available
  const soundsAvailable = {
    timerComplete: false,
    restComplete: false,
    countdownBeep: false,
    workoutComplete: false,
  };

  // Check if sounds are available
  const checkSoundAvailability = () => {
    Object.entries(audioElements).forEach(([key, audio]) => {
      audio.addEventListener("error", () => {
        console.warn(`Sound file not available: ${key}`);
        soundsAvailable[key as keyof typeof soundsAvailable] = false;
      });

      audio.addEventListener("canplaythrough", () => {
        soundsAvailable[key as keyof typeof soundsAvailable] = true;
      });

      // Try to load the sound
      audio.load();
    });
  };

  // Call immediately
  checkSoundAvailability();

  // Play a tone using Web Audio API (fallback for missing sound files)
  const playTone = (
    frequency = 440,
    duration = 0.1,
    type: OscillatorType = "sine",
    volume = 0.5
  ) => {
    if (!audioContext) return;

    try {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.type = type;
      oscillator.frequency.value = frequency;
      gainNode.gain.value = volume;

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.start();
      oscillator.stop(audioContext.currentTime + duration);
    } catch (e) {
      console.error("Error playing tone:", e);
    }
  };

  // Fallback sounds using Web Audio API
  const fallbackSounds = {
    timerComplete: () => {
      // Higher pitched success sound
      playTone(880, 0.1, "sine", 0.5);
      setTimeout(() => playTone(1320, 0.2, "sine", 0.5), 100);
    },
    restComplete: () => {
      // Alert sound
      playTone(440, 0.1, "square", 0.3);
      setTimeout(() => playTone(660, 0.2, "square", 0.3), 150);
      setTimeout(() => playTone(880, 0.3, "square", 0.3), 350);
    },
    countdownBeep: () => {
      // Simple beep
      playTone(440, 0.1, "sine", 0.3);
    },
    workoutComplete: () => {
      // Celebratory sound
      playTone(523.25, 0.1, "sine", 0.5); // C5
      setTimeout(() => playTone(659.25, 0.1, "sine", 0.5), 100); // E5
      setTimeout(() => playTone(783.99, 0.1, "sine", 0.5), 200); // G5
      setTimeout(() => playTone(1046.5, 0.3, "sine", 0.5), 300); // C6
    },
  };

  return {
    // Audio context methods
    getAudioContext: () => audioContext,

    resumeAudioContext: () => {
      if (audioContext && audioContext.state === "suspended") {
        audioContext
          .resume()
          .then(() => console.log("AudioContext resumed successfully"))
          .catch((err) => console.error("Failed to resume AudioContext:", err));
      }
    },

    // Generic tone player
    playTone,

    // Sound playback methods with fallbacks
    playTimerComplete: () => {
      try {
        if (soundsAvailable.timerComplete) {
          audioElements.timerComplete.currentTime = 0;
          audioElements.timerComplete.play().catch((err) => {
            console.error("Error playing timer sound:", err);
            fallbackSounds.timerComplete();
          });
        } else {
          fallbackSounds.timerComplete();
        }
      } catch (err) {
        console.error("Error playing timer sound:", err);
        fallbackSounds.timerComplete();
      }
    },

    playRestComplete: () => {
      try {
        if (soundsAvailable.restComplete) {
          audioElements.restComplete.currentTime = 0;
          audioElements.restComplete.play().catch((err) => {
            console.error("Error playing rest sound:", err);
            fallbackSounds.restComplete();
          });
        } else {
          fallbackSounds.restComplete();
        }
      } catch (err) {
        console.error("Error playing rest sound:", err);
        fallbackSounds.restComplete();
      }
    },

    playCountdownBeep: () => {
      try {
        if (soundsAvailable.countdownBeep) {
          audioElements.countdownBeep.currentTime = 0;
          audioElements.countdownBeep.play().catch((err) => {
            console.error("Error playing countdown sound:", err);
            fallbackSounds.countdownBeep();
          });
        } else {
          fallbackSounds.countdownBeep();
        }
      } catch (err) {
        console.error("Error playing countdown sound:", err);
        fallbackSounds.countdownBeep();
      }
    },

    playWorkoutComplete: () => {
      try {
        if (soundsAvailable.workoutComplete) {
          audioElements.workoutComplete.currentTime = 0;
          audioElements.workoutComplete.play().catch((err) => {
            console.error("Error playing workout complete sound:", err);
            fallbackSounds.workoutComplete();
          });
        } else {
          fallbackSounds.workoutComplete();
        }
      } catch (err) {
        console.error("Error playing workout complete sound:", err);
        fallbackSounds.workoutComplete();
      }
    },
  };
};

// Create a singleton sound player
const soundPlayer = createSoundPlayer();

export function useWorkoutSounds() {
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [audioContextState, setAudioContextState] = useState("suspended");
  const soundInitializedRef = useRef(false);
  const isMounted = useRef(true);

  // Check audio context state
  const checkAudioContextState = useCallback(() => {
    const audioContext = soundPlayer.getAudioContext();
    const state = audioContext?.state || "suspended";
    setAudioContextState(state);
    return state;
  }, []);

  // Resume audio context
  const resumeAudioContext = useCallback(() => {
    soundPlayer.resumeAudioContext();
    checkAudioContextState();
  }, [checkAudioContextState]);

  // Initialize audio context
  useEffect(() => {
    checkAudioContextState();
  }, [checkAudioContextState]);

  // Toggle sound on/off
  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const newState = !prev;
      toast.success(newState ? "Sound enabled" : "Sound disabled");
      return newState;
    });
  }, []);

  // Play a sound if enabled
  const playSound = useCallback(
    (type: "timer" | "rest" | "countdown" | "complete") => {
      if (!isMounted.current || !soundEnabled) return;

      try {
        // Try to resume audio context if suspended
        if (audioContextState === "suspended") {
          resumeAudioContext();
        }

        switch (type) {
          case "timer":
            soundPlayer.playTimerComplete();
            break;
          case "rest":
            soundPlayer.playRestComplete();
            break;
          case "countdown":
            soundPlayer.playCountdownBeep();
            break;
          case "complete":
            soundPlayer.playWorkoutComplete();
            break;
        }
      } catch (error) {
        console.error("Error playing sound:", error);
      }
    },
    [soundEnabled, audioContextState, resumeAudioContext]
  );

  // Test a specific sound
  const testSound = useCallback(
    (name: string) => {
      console.log(`Testing sound: ${name}`);
      playSound(name as "timer" | "rest" | "countdown" | "complete");
    },
    [playSound]
  );

  // Initialize audio context on first user interaction
  useEffect(() => {
    const handleUserInteraction = () => {
      if (soundInitializedRef.current) return;

      // Try to play a silent sound to initialize audio context
      try {
        soundPlayer.playTone(0, 0.1, "sine", 0.001);
        soundInitializedRef.current = true;
        console.log("Audio context initialized successfully");
        checkAudioContextState();

        // Remove event listeners after initialization
        document.removeEventListener("click", handleUserInteraction);
        document.removeEventListener("touchstart", handleUserInteraction);
      } catch (e) {
        console.error("Failed to initialize audio context:", e);
      }
    };

    // Add event listeners for user interaction
    document.addEventListener("click", handleUserInteraction);
    document.addEventListener("touchstart", handleUserInteraction);

    return () => {
      // Clean up event listeners
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
    };
  }, [checkAudioContextState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return {
    soundEnabled,
    audioContextState,
    toggleSound,
    playSound,
    checkAudioContextState,
    resumeAudioContext,
    testSound,
  };
}
